/**
 * FormatterMixin - Vue组件格式化器Mixin
 *
 * 提供统一的item值格式化能力，消除JJForm组件中的代码重复
 * 支持Vue 2.5响应式特性，兼容ES2017语法
 *
 * @author System Architect
 * @created 2025-09-16
 * @version 1.0.0
 *
 * 使用方式：
 * ```javascript
 * import { FormatterMixin } from '@/utils/lowcode/mixins/FormatterMixin'
 *
 * export default {
 *   mixins: [FormatterMixin],
 *   computed: {
 *     processedValue() {
 *       return this.formatItemValue(this.item, this.$attrs.value)
 *     }
 *   }
 * }
 * ```
 */

import { getGlobalFormatter } from '@/utils/lowcode/core/ItemValueFormatter'

/**
 * 格式化器Mixin - 为Vue组件提供统一的格式化能力
 */
export const FormatterMixin = {
  data() {
    return {
      // 延迟初始化标志
      _formatterInitialized: false
    }
  },

  computed: {
    /**
     * 获取格式化器实例 - 延迟初始化优化性能
     * @returns {ItemValueFormatter}
     */
    itemFormatter() {
      if (!this._formatterInitialized) {
        // 延迟初始化，避免影响组件创建性能
        this._formatterInstance = getGlobalFormatter()
        this._formatterInitialized = true
      }
      return this._formatterInstance
    }
  },

  methods: {
    /**
     * 格式化item值 - 主要接口方法
     * @param {Object} item - 配置项对象
     * @param {*} value - 要格式化的值（可选，默认使用item.value）
     * @returns {*} 格式化后的值，失败时返回原始值
     */
    formatItemValue(item, value) {
      if (!item || !this.itemFormatter) {
        return value !== undefined ? value : (item && item.value) || null
      }

      try {
        const formatted = this.itemFormatter.formatItemValue(item, value)
        // 格式化失败时返回原始值，确保向后兼容
        return formatted !== null ? formatted : (value !== undefined ? value : item.value)
      } catch (error) {
        console.warn('[FormatterMixin] 格式化失败，返回原始值:', item.key, value, error)
        return value !== undefined ? value : (item && item.value) || null
      }
    },

    /**
     * 检查是否需要格式化 - 性能优化用
     * @param {Object} item - 配置项对象
     * @param {*} value - 要检查的值
     * @returns {boolean}
     */
    shouldFormatValue(item, value) {
      if (!item || !this.itemFormatter) {
        return false
      }

      try {
        return this.itemFormatter.shouldFormat(item, value)
      } catch (error) {
        console.warn('[FormatterMixin] shouldFormatValue检查失败:', item.key, value, error)
        return false
      }
    },

    /**
     * 批量格式化 - 用于处理多个item配置
     * @param {Array} items - item配置数组
     * @param {Object} valueOverrides - 值覆盖对象 {key: value}
     * @returns {Object} 格式化结果对象 {key: formattedValue}
     */
    formatBatchValues(items, valueOverrides) {
      if (!Array.isArray(items) || !this.itemFormatter) {
        return {}
      }

      try {
        return this.itemFormatter.formatBatchWithOverrides(items, valueOverrides || {})
      } catch (error) {
        console.warn('[FormatterMixin] 批量格式化失败:', error)
        return {}
      }
    },

    /**
     * 格式化日期值 - 便捷方法，专门处理日期类型
     * @param {string} dateValue - 日期字符串值
     * @param {Object} options - 格式化选项（可选）
     * @returns {string|null} 格式化后的日期字符串
     */
    formatDateValue(dateValue, options) {
      if (!dateValue || typeof dateValue !== 'string') {
        return dateValue
      }

      const dateItem = {
        type: 'date',
        value: dateValue,
        key: 'temp_date_' + Date.now(),
        formatter: options && options.formatter ? options.formatter : undefined
      }

      return this.formatItemValue(dateItem, dateValue)
    },

    /**
     * 清理格式化器缓存 - 可选的性能管理方法
     */
    clearFormatterCache() {
      if (this.itemFormatter && typeof this.itemFormatter.clearResultCache === 'function') {
        this.itemFormatter.clearResultCache()
      }
    }
  },

  /**
   * 组件销毁前清理资源
   */
  beforeDestroy() {
    // 清理引用，避免内存泄漏
    if (this._formatterInstance) {
      this._formatterInstance = null
    }
    this._formatterInitialized = false
  }
}

/**
 * 专用于JJForm的Mixin - 提供更具体的processedValue计算属性
 */
export const JJFormFormatterMixin = {
  mixins: [FormatterMixin],

  computed: {
    /**
     * 处理后的值 - 替换JJForm组件中的重复逻辑
     * 兼容原有的$attrs.value访问方式
     */
    processedValue() {
      const { item, $attrs } = this
      let originalValue = $attrs && $attrs.value

      // 🔧 修复：转换 ISO 8601 日期格式
      // 支持格式：2025-11-06T00:00:00 或 2025-11-06T00:00:00.000Z
      // 转换为：2025-11-06 00:00:00 或保持 2025-11-06 不变
      if (item && item.type === 'date' && originalValue && typeof originalValue === 'string') {
        if (originalValue.includes('T')) {
          // 将 ISO 8601 格式转换为普通格式
          originalValue = originalValue
            .replace('T', ' ') // T 替换为空格
            .replace(/\.\d{3}Z?$/, '') // 移除毫秒和 Z
            .replace(/[+-]\d{2}:\d{2}$/, '') // 移除时区信息如 +08:00
            .trim()
        }
      }

      // 🔧 修复：转换 ISO 8601 日期格式
      // 支持格式：2025-11-06T00:00:00 或 2025-11-06T00:00:00.000Z
      // 转换为：2025-11-06 00:00:00 或保持 2025-11-06 不变
      let processedValue = originalValue
      if (item && item.type === 'date' && originalValue && typeof originalValue === 'string') {
        if (originalValue.includes('T')) {
          // 将 ISO 8601 格式转换为普通格式
          processedValue = originalValue
            .replace('T', ' ') // T 替换为空格
            .replace(/\.\d{3}Z?$/, '') // 移除毫秒和 Z
            .replace(/[+-]\d{2}:\d{2}$/, '') // 移除时区信息如 +08:00
            .trim()
        }
      }

      // 只处理需要格式化的情况，优化性能
      if (!this.shouldFormatValue(item, processedValue)) {
        return processedValue
      }

      return this.formatItemValue(item, processedValue)
    },

    /**
     * 原始值访问器 - 保持向后兼容
     */
    originalValue() {
      return this.$attrs && this.$attrs.value
    }
  }
}

/**
 * 专用于CommonList搜索的Mixin - 处理搜索配置格式化
 */
export const SearchFormatterMixin = {
  mixins: [FormatterMixin],

  methods: {
    /**
     * 处理搜索配置值 - 替换CommonList中的重复逻辑
     * @param {Array} searchHeaderList - 搜索配置列表
     * @returns {Object} 处理后的查询对象
     */
    processSearchConfigValues(searchHeaderList) {
      if (!Array.isArray(searchHeaderList)) {
        return {}
      }

      try {
        // 使用新架构：统一批量处理
        const processedQuery = this.formatBatchValues(searchHeaderList)

        // 兼容处理：如果新架构失败，回退到兼容模式
        if (Object.keys(processedQuery).length === 0 && searchHeaderList.length > 0) {
          return this.processSearchConfigValuesLegacy(searchHeaderList)
        }

        return processedQuery
      } catch (error) {
        console.error('[SearchFormatterMixin] 搜索配置处理失败，回退到兼容模式:', error)
        return this.processSearchConfigValuesLegacy(searchHeaderList)
      }
    },

    /**
     * 向后兼容的搜索配置处理方法 - 保留原有逻辑作为备用
     */
    processSearchConfigValuesLegacy(searchHeaderList) {
      const result = {}

      searchHeaderList.forEach(function(item) {
        // ES2017兼容：使用function替代箭头函数
        if (item && item.type === 'date' && typeof item.value === 'string' && item.value && item.key) {
          // 仅处理包含getDateprocessing的日期项
          if (item.value.includes('getDateprocessing(')) {
            try {
              const match = item.value.match(/getDateprocessing\(([^)]+)\)/)
              if (match) {
                const param = match[1]
                const paramValue = param.startsWith("'") && param.endsWith("'")
                  ? param.slice(1, -1)
                  : parseInt(param)

                // 导入getDateprocessing函数
                const { getDateprocessing } = require('@/utils/index')
                result[item.key] = getDateprocessing(paramValue)
              }
            } catch (e) {
              console.warn('搜索配置legacy处理失败:', item.key, item.value, e)
            }
          }
        }
      })

      return result
    }
  }
}

/**
 * 导出默认mixin
 */
export default FormatterMixin
